import { Box, Grid, Tooltip, Typography, useTheme, type Theme } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import type { ReactNode } from "react";
import type { AttackerSpend, NeverAuthenticated, TelemetrySummary } from "@/types";
import AttackerSpendTile from "./AttackerSpendTile.tsx";
import NeverAuthenticatedTile from "./NeverAuthenticatedTile.tsx";

interface TelemetryCardsProps {
  readonly data: TelemetrySummary;
  readonly auditMode: boolean;
  readonly neverAuthenticated: NeverAuthenticated | null;
  readonly neverAuthenticatedAsOf?: string | null;
  readonly queryCoverageSignificant: boolean;
  readonly onNeverAuthClick: () => void;
  readonly attackerSpend: AttackerSpend | null;
  readonly onAttackerSpendClick: () => void;
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// neutral: grey always. security: up=red/down=green (more = bad).
// shield:  up=green/down=red (more = good, inverted vs. security).
type TrendColor = "neutral" | "security" | "shield";

interface MetricCardProps {
  readonly label: string;
  readonly value: string;
  readonly subvalue?: string;
  readonly trend: number;
  readonly trendColor: TrendColor;
  readonly trendSuffix?: string;
  readonly icon?: ReactNode;
}

function MetricCard({
  label,
  value,
  subvalue,
  trend,
  trendColor,
  trendSuffix = "%",
  icon,
}: MetricCardProps) {
  const theme = useTheme();
  const isPositive = trend >= 0;
  const color = resolveTrendColor(theme, trendColor, isPositive);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {icon}
        <Typography
          variant="overline"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "0.625rem",
            letterSpacing: "0.08em",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: "-0.02em", fontSize: "1.75rem" }}
        >
          {value}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color }}>
          <TrendArrow trend={trend} />
          <Typography variant="caption" sx={{ fontWeight: 600 }} data-testid="metric-trend">
            {isPositive ? "+" : ""}
            {trend}
            {trendSuffix}
          </Typography>
        </Box>
      </Box>
      {subvalue !== undefined && (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, display: "block", mt: 0.25 }}
        >
          {subvalue}
        </Typography>
      )}
    </Box>
  );
}

function TrendArrow({ trend }: { readonly trend: number }) {
  if (trend === 0) return <TrendingFlatIcon sx={{ fontSize: 14 }} />;
  if (trend > 0) return <TrendingUpIcon sx={{ fontSize: 14 }} />;
  return <TrendingDownIcon sx={{ fontSize: 14 }} />;
}

function resolveTrendColor(theme: Theme, trendColor: TrendColor, isPositive: boolean): string {
  if (trendColor === "neutral") return theme.palette.text.secondary;
  if (trendColor === "shield") {
    return isPositive ? theme.palette.secondary.main : theme.palette.error.main;
  }
  return isPositive ? theme.palette.error.main : theme.palette.secondary.main;
}

const TILE_SIZE = { xs: 6, md: "grow" } as const;

function ComparisonLabel({ data }: { readonly data: TelemetrySummary }) {
  return (
    <Tooltip
      title={`${data.current_period} compared to ${data.previous_period}`}
      arrow
      placement="top-start"
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          mb: 1,
          display: "inline-block",
          cursor: "help",
          borderBottom: "1px dashed",
          borderColor: "text.disabled",
        }}
      >
        {data.comparison_label}
      </Typography>
    </Tooltip>
  );
}

function TooltipTile({
  title,
  children,
}: {
  readonly title: NonNullable<ReactNode>;
  readonly children: ReactNode;
}) {
  return (
    <Tooltip title={title} arrow>
      <Box sx={{ cursor: "help" }}>{children}</Box>
    </Tooltip>
  );
}

function formatSubsetShare(subset: number, total: number): string | undefined {
  if (total <= 0) return undefined;
  const pct = Math.round((subset / total) * 100);
  return `${formatCompact(subset)} of ${formatCompact(total)} (${pct}%)`;
}

function TelemetryGrid({
  data,
  auditMode,
  neverAuthenticated,
  neverAuthenticatedAsOf,
  queryCoverageSignificant,
  onNeverAuthClick,
  attackerSpend,
  onAttackerSpendClick,
}: TelemetryCardsProps) {
  return (
    <Grid container spacing={2}>
      <Grid size={TILE_SIZE}>
        <MetricCard label="Successful" value={formatCompact(data.successful)} trend={data.successful_trend} trendColor="neutral" />
      </Grid>
      <Grid size={TILE_SIZE}>
        <MetricCard label="Blocked" value={formatCompact(data.blocked)} trend={data.blocked_trend} trendColor="security" />
      </Grid>
      <Grid size={TILE_SIZE}>
        <TooltipTile title="Username, password, or 2FA attempts that did not complete successfully.">
          <MetricCard label="Authentication Failures" value={formatCompact(data.auth_failures)} trend={data.auth_failures_trend} trendColor="security" />
        </TooltipTile>
      </Grid>
      <Grid size={TILE_SIZE}>
        <TooltipTile title="Subset of Authentication Failures where credentials were correct but the second factor step failed.">
          <MetricCard
            label="Failed Second Factor"
            value={formatCompact(data.failed_second_factor)}
            subvalue={formatSubsetShare(data.failed_second_factor, data.auth_failures)}
            trend={data.failed_second_factor_trend}
            trendColor="security"
          />
        </TooltipTile>
      </Grid>
      {queryCoverageSignificant && neverAuthenticated && (
        <Grid size={TILE_SIZE}>
          <NeverAuthenticatedTile data={neverAuthenticated} asOf={neverAuthenticatedAsOf} onClick={onNeverAuthClick} />
        </Grid>
      )}
      {attackerSpend && (
        <Grid size={TILE_SIZE}>
          <AttackerSpendTile data={attackerSpend} onClick={onAttackerSpendClick} />
        </Grid>
      )}
      {!auditMode && (
        <Grid size={TILE_SIZE}>
          <ShieldEfficiencyTile data={data} />
        </Grid>
      )}
    </Grid>
  );
}

function ShieldEfficiencyTile({ data }: { readonly data: TelemetrySummary }) {
  const icon = <ShieldOutlinedIcon sx={{ fontSize: 14, color: "secondary.main" }} />;
  const value = data.shield_efficiency === null ? "—" : `${data.shield_efficiency.toFixed(1)}%`;

  return (
    <TooltipTile title={<ShieldEfficiencyExplanation data={data} />}>
      <MetricCard
        label="Shield Efficiency"
        value={value}
        trend={data.shield_efficiency_trend}
        trendColor="shield"
        trendSuffix="pp"
        icon={icon}
      />
    </TooltipTile>
  );
}

function ShieldEfficiencyExplanation({ data }: { readonly data: TelemetrySummary }) {
  const hasTraffic = data.shield_efficiency !== null && data.shield_efficiency_denominator > 0;
  return (
    <Box sx={{ p: 0.5, fontSize: "0.75rem", lineHeight: 1.5 }}>
      <Box sx={{ fontWeight: 600, mb: 0.5 }}>
        Share of non-legitimate traffic that Shield blocked up front.
      </Box>
      {hasTraffic ? (
        <>
          <Box sx={{ fontFamily: "monospace", mb: 0.5 }}>
            blocked &divide; (blocked + auth failures)
          </Box>
          <Box sx={{ fontFamily: "monospace" }}>
            {data.blocked.toLocaleString()} &divide;{" "}
            {data.shield_efficiency_denominator.toLocaleString()} ={" "}
            {data.shield_efficiency?.toFixed(1)}%
          </Box>
        </>
      ) : (
        <Box sx={{ opacity: 0.8 }}>No non-legitimate traffic in this window.</Box>
      )}
      <Box sx={{ mt: 0.75, opacity: 0.8 }}>
        Successful logins are excluded so growth in legitimate traffic does not dilute the score.
      </Box>
      <Box sx={{ mt: 0.5, opacity: 0.8 }}>
        Trend is the percentage-point change vs. {data.previous_period}.
      </Box>
    </Box>
  );
}

export default function TelemetryCards({
  data,
  auditMode,
  neverAuthenticated,
  neverAuthenticatedAsOf,
  queryCoverageSignificant,
  onNeverAuthClick,
  attackerSpend,
  onAttackerSpendClick,
}: TelemetryCardsProps) {
  return (
    <Box>
      <ComparisonLabel data={data} />
      <TelemetryGrid
        data={data}
        auditMode={auditMode}
        neverAuthenticated={neverAuthenticated}
        neverAuthenticatedAsOf={neverAuthenticatedAsOf}
        queryCoverageSignificant={queryCoverageSignificant}
        onNeverAuthClick={onNeverAuthClick}
        attackerSpend={attackerSpend}
        onAttackerSpendClick={onAttackerSpendClick}
      />
    </Box>
  );
}
